/**
 * Proveedores y pedidos de compra.
 *
 * El módulo tenía siete operaciones en la API y ninguna forma de invocarlas.
 * Y arrastraba un agujero más profundo: la tabla `supplier` existía desde la
 * migración 0014 y **nadie insertaba en ella**. Sin proveedores no hay
 * pedidos, así que compras era inalcanzable de raíz, no sólo sin pantalla.
 *
 * Aquí están las dos piezas que abren el módulo —dar de alta proveedores y
 * emitir un pedido— y la aprobación, que es la que decide si el pedido sale.
 */

import { useCallback, useEffect, useState } from 'react';
import { hasPermission, useSession } from '../session/session-context.js';
import { ApiError } from '../session/api-error.js';
import { FormSection, SelectField, TextField } from '../forms/fields.js';

interface Supplier {
  id: string;
  code: string;
  legal_name: string;
  tax_id: string | null;
  lead_days: number;
  is_active: boolean;
}

interface Warehouse {
  id: string;
  name: string;
  kind: string;
}

interface Line {
  description: string;
  quantity: string;
  unitPrice: string;
}

const EMPTY_LINE: Line = { description: '', quantity: '', unitPrice: '' };

export function PurchasingActions({ onDone }: { onDone: () => void }): React.ReactNode {
  const { api, user } = useSession();

  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Alta de proveedor
  const [code, setCode] = useState('');
  const [legalName, setLegalName] = useState('');
  const [taxId, setTaxId] = useState('');
  const [leadDays, setLeadDays] = useState('7');

  // Pedido
  const [supplierId, setSupplierId] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [expectedOn, setExpectedOn] = useState('');
  const [lines, setLines] = useState<Line[]>([{ ...EMPTY_LINE }]);

  const canCreate = hasPermission(user, 'purchasing.create');

  const reload = useCallback(async () => {
    const [s, w] = await Promise.all([
      api.get<{ items: Supplier[] }>('/purchasing/suppliers').catch(() => ({ items: [] })),
      api.get<{ items: Warehouse[] }>('/inventory/warehouses').catch(() => ({ items: [] })),
    ]);
    setSuppliers(s.items);
    setWarehouses(w.items);
  }, [api]);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function createSupplier(): Promise<void> {
    setError(null);
    setBusy(true);
    try {
      await api.post('/purchasing/suppliers', {
        code,
        legalName,
        ...(taxId === '' ? {} : { taxId }),
        ...(leadDays === '' ? {} : { leadDays: Number(leadDays) }),
      });
      setNotice(`Proveedor ${legalName} dado de alta.`);
      setCode('');
      setLegalName('');
      setTaxId('');
      await reload();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'No se ha podido crear el proveedor.');
    } finally {
      setBusy(false);
    }
  }

  async function createOrder(): Promise<void> {
    const usable = lines.filter((l) => l.description !== '' && l.quantity !== '' && l.unitPrice !== '');
    if (usable.length === 0) {
      setError('Un pedido de compra sin líneas no pide nada.');
      return;
    }

    setError(null);
    setBusy(true);
    try {
      const created = await api.post<{ number: string; requiresApproval: boolean }>(
        '/purchasing/orders',
        {
          supplierId,
          ...(warehouseId === '' ? {} : { warehouseId }),
          ...(expectedOn === '' ? {} : { expectedOn }),
          lines: usable.map((l) => ({
            description: l.description,
            quantity: l.quantity,
            unitPrice: l.unitPrice,
          })),
        },
      );
      setNotice(
        created.requiresApproval
          ? `Pedido ${created.number} creado. Supera el umbral: necesita aprobación antes de salir.`
          : `Pedido ${created.number} creado.`,
      );
      setLines([{ ...EMPTY_LINE }]);
      setExpectedOn('');
      onDone();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'No se ha podido crear el pedido.');
    } finally {
      setBusy(false);
    }
  }

  function setLine(index: number, patch: Partial<Line>): void {
    setLines((current) => current.map((l, i) => (i === index ? { ...l, ...patch } : l)));
  }

  if (!canCreate) return null;

  const supplierOptions = suppliers
    .filter((s) => s.is_active)
    .map((s) => ({ value: s.id, label: `${s.code} · ${s.legal_name}` }));
  const warehouseOptions = warehouses.map((w) => ({ value: w.id, label: w.name }));

  return (
    <>
      {error !== null && (
        <p className="screen__error" role="alert">
          {error}
        </p>
      )}
      {notice !== null && (
        <p className="screen__note screen__note--strong" role="status">
          {notice}
        </p>
      )}

      <section className="panel">
        <h3 className="panel__title">Proveedores ({suppliers.length})</h3>

        {suppliers.length > 0 && (
          <div className="scroller">
            <table className="table__grid table__grid--compact">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Razón social</th>
                  <th>NIF</th>
                  <th>Plazo de entrega</th>
                </tr>
              </thead>
              <tbody>
                {suppliers.map((s) => (
                  <tr key={s.id}>
                    <td>{s.code}</td>
                    <td>{s.legal_name}</td>
                    <td>{s.tax_id ?? '—'}</td>
                    <td>{s.lead_days} días</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="form">
          <FormSection title="Nuevo proveedor">
            <TextField label="Código" value={code} onChange={setCode} required />
            <TextField label="Razón social" value={legalName} onChange={setLegalName} required />
            <TextField label="NIF" value={taxId} onChange={setTaxId} />
            <TextField
              label="Plazo de entrega (días)"
              type="number"
              value={leadDays}
              onChange={setLeadDays}
              help="Lo que tarda en servir. Sin esto, el punto de pedido es adivinación."
            />
            <button
              className="button button--primary"
              type="button"
              disabled={busy || code === '' || legalName === ''}
              onClick={() => void createSupplier()}
            >
              {busy ? 'Guardando…' : 'Crear proveedor'}
            </button>
          </FormSection>
        </div>
      </section>

      <section className="panel">
        <h3 className="panel__title">Nuevo pedido de compra</h3>

        {suppliers.length === 0 ? (
          <p className="screen__note">Antes hace falta al menos un proveedor.</p>
        ) : (
          <div className="form">
            <FormSection title="Cabecera">
              <SelectField
                label="Proveedor"
                value={supplierId}
                onChange={setSupplierId}
                options={supplierOptions}
                emptyLabel="Seleccione…"
                required
              />
              <SelectField
                label="Almacén de destino"
                value={warehouseId}
                onChange={setWarehouseId}
                options={warehouseOptions}
                emptyLabel="Sin especificar"
                help="Dónde entrará el material cuando se reciba."
              />
              <TextField
                label="Fecha prevista"
                type="date"
                value={expectedOn}
                onChange={setExpectedOn}
              />
            </FormSection>

            <FormSection title="Líneas">
              {lines.map((line, index) => (
                <div className="attachments__item" key={index}>
                  <TextField
                    label={`Concepto ${index + 1}`}
                    value={line.description}
                    onChange={(v) => setLine(index, { description: v })}
                  />
                  <TextField
                    label="Cantidad"
                    value={line.quantity}
                    onChange={(v) => setLine(index, { quantity: v })}
                  />
                  <TextField
                    label="Precio unitario"
                    value={line.unitPrice}
                    onChange={(v) => setLine(index, { unitPrice: v })}
                  />
                </div>
              ))}
              <button
                className="button button--quiet"
                type="button"
                onClick={() => setLines((c) => [...c, { ...EMPTY_LINE }])}
              >
                Añadir línea
              </button>
            </FormSection>

            <button
              className="button button--primary"
              type="button"
              disabled={busy || supplierId === ''}
              onClick={() => void createOrder()}
            >
              {busy ? 'Creando…' : 'Crear pedido'}
            </button>
          </div>
        )}
      </section>
    </>
  );
}
